import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { FileText, Shield, Lightbulb, TrendingUp } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function PatentApplication() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Lightbulb, title: 'Innovation Protection', description: 'Secure exclusive rights to your invention for up to 20 years.' },
    { icon: Shield, title: 'Legal Shield', description: 'Prevent others from making, using, or selling your invention.' },
    { icon: FileText, title: 'Professional Drafting', description: 'Expert preparation of patent specifications and claims.' },
    { icon: TrendingUp, title: 'Business Value', description: 'Increase company valuation with patented technology.' },
  ];

  const faqs = [
    { question: 'What can be patented?', answer: 'You can patent new and useful processes, machines, manufactures, compositions of matter, or improvements thereof. The invention must be novel, non-obvious, and useful.' },
    { question: 'How long does the patent process take?', answer: 'From filing to grant typically takes 2-4 years, though this varies by technology field and application complexity. Expedited examination is available for an additional fee.' },
    { question: 'What are the types of patents?', answer: 'The three types are utility patents (for functional inventions), design patents (for ornamental designs), and plant patents (for new plant varieties).' },
    { question: 'How much does a patent cost?', answer: 'Costs vary based on complexity, but typically include filing fees, attorney fees for preparation, and maintenance fees over the patent\'s life. A comprehensive patent can cost $5,000-$15,000+.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Patent Application Submission</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">File your patent application with professional guidance and secure your invention rights.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why File a Patent Application?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Protect your innovative ideas with professional patent services.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about patent applications.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to File Your Patent?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your invention with professional patent application services.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="patent_application" />
      <Footer />
    </div>
  );
}